import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Add auth token to requests
client.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface ComplexClaim {
  claimID: string;
  clientID: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  ipAddress: string | null;
  claimStartDate: string | null;
  agreementNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postcode: string | null;
  dateOfBirth: string | null;
  previousAddress1Line1: string | null;
  previousAddress1Line2: string | null;
  previousAddress1City: string | null;
  previousAddress1Postcode: string | null;
  previousAddress2Line1: string | null;
  previousAddress2Line2: string | null;
  previousAddress2City: string | null;
  previousAddress2Postcode: string | null;
  craFinanceLender: string | null;
  craAgreementNumberOriginal: string | null;
  craCreditStartDate: string | null;
  craCreditEndDate: string | null;
  craDateOfAgreement: string | null;
  craAgreementType: string | null;
  craOriginalLoanAmount: string | null;
  craCurrentBalance: string | null;
  craTerm: string | null;
  craMonthlyPayment: string | null;
  status: string | null;
  previouslyKnownNames: string | null;
  formOfAuthorityUrl: string | null;
  damageBasedAgreementUrl: string | null;
  locDocUrl: string | null;
  clientIdDocUrl: string | null;
  signatureAuditTrailUrl: string | null;
}

export interface ComplexEmail {
  id: string;
  client_reference: string | null;
  to: string | null;
  from: string | null;
  subject: string | null;
  content: string | null;
  type: string | null;
  bounced_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  message_id: string | null;
  sent_at: string | null;
  received_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ComplexClaimsSearchParams {
  q?: string;
  page?: number;
  limit?: number;
}

export interface ComplexClaimsSearchResponse {
  success: boolean;
  items: ComplexClaim[];
  page: number;
  limit: number;
  total: number;
}

interface ClaimDetailResponse {
  success: boolean;
  item: ComplexClaim;
}

interface ClaimEmailsResponse {
  success: boolean;
  items: ComplexEmail[];
}

// API Functions
export async function searchComplexClaims(
  params: ComplexClaimsSearchParams
): Promise<ComplexClaimsSearchResponse> {
  const response = await client.get<ComplexClaimsSearchResponse>('/ui/complex-claims', { params });
  if (!response.data.success) {
    throw new Error('Failed to search complex claims');
  }
  return response.data;
}

export async function getComplexClaim(id: string): Promise<ComplexClaim> {
  const response = await client.get<ClaimDetailResponse>(
    `/ui/complex-claims/${encodeURIComponent(id)}`
  );
  if (!response.data.success) {
    throw new Error('Failed to load claim');
  }
  return response.data.item;
}

export async function getComplexClaimEmails(id: string): Promise<ComplexEmail[]> {
  const response = await client.get<ClaimEmailsResponse>(
    `/ui/complex-claims/${encodeURIComponent(id)}/emails`
  );
  if (!response.data.success) {
    throw new Error('Failed to load claim emails');
  }
  return response.data.items;
}
